import { EnrollGroup, CourseOption } from '../../types/course.response';
import { collectScheduledMeetings, hasMeetingConflict } from '../../utils/scheduleUtils';

interface EnrollGroupInfoBarProps {
    enrollGroup: EnrollGroup;
    userCourses?: CourseOption[];
}

const OPEN_STATUS_LABELS: Record<string, string> = {
    C: 'Closed',
    W: 'Waitlist',
    O: 'Open',
};

function joinWithAnd(items: string[]): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function EnrollGroupInfoBar({ enrollGroup, userCourses = [] }: EnrollGroupInfoBarProps) {
    const { creditsMinimum, creditsMaximum, classSections, topic } = enrollGroup;

    const credits = creditsMinimum === creditsMaximum
        ? `${creditsMinimum} cr`
        : `${creditsMinimum}–${creditsMaximum} cr`;

    const firstSection = classSections[0];
    const firstType = firstSection?.sectionType;
    const sameTypeSections = firstType
        ? classSections.filter(cs => cs.sectionType === firstType)
        : [];

    const scheduledMeetings = collectScheduledMeetings(userCourses, enrollGroup.semester);

    const selectedSection = sameTypeSections.find(
        cs => !cs.meetings.some(m => scheduledMeetings.some(o => hasMeetingConflict(m, o)))
    ) ?? firstSection;

    const firstMeeting = selectedSection?.meetings[0];

    const sectionInfo = selectedSection
        ? `${selectedSection.sectionType} ${selectedSection.sectionNumber}`
        : null;

    const time = firstMeeting?.pattern && firstMeeting?.timeStart
        ? `${firstMeeting.pattern} ${firstMeeting.timeStart}`
        : null;

    const instructors = firstMeeting?.instructors ?? [];
    const firstInstructor = instructors[0];
    const instructorName = firstInstructor
        ? [firstInstructor.firstName, firstInstructor.middleName, firstInstructor.lastName]
            .filter(Boolean)
            .join(' ')
        : null;
    const moreInstructorsCount = instructors.length > 1 ? instructors.length - 1 : 0;

    const openStatus = selectedSection?.openStatus
        ? (OPEN_STATUS_LABELS[selectedSection.openStatus] ?? selectedSection.openStatus)
        : null;

    const title = topic || null;

    // Compute per-type stats
    const allTypes = Array.from(new Set(classSections.map(cs => cs.sectionType).filter((t): t is string => t !== null)));
    const typeStats = allTypes.map(type => {
        const sections = classSections.filter(cs => cs.sectionType === type);
        const fitCount = sections.filter(
            cs => !cs.meetings.some(m => scheduledMeetings.some(o => hasMeetingConflict(m, o)))
        ).length;
        const openCount = sections.filter(cs => cs.openStatus === 'O').length;
        return { type, total: sections.length, fitCount, openCount };
    });

    // "Also requires X and Y" line for the header (only when multiple types)
    const otherTypes = allTypes.filter(t => t !== firstType);
    const alsoRequiresLine = otherTypes.length > 0
        ? `Also requires ${joinWithAnd(otherTypes)}`
        : null;

    // Color logic: time
    const selectedFits = !selectedSection?.meetings.some(
        m => scheduledMeetings.some(o => hasMeetingConflict(m, o))
    );
    const allTypesFit = typeStats.every(s => s.fitCount > 0);
    const timeColor = allTypesFit ? 'text-green-600' : (!selectedFits ? 'text-red-600' : 'text-gray-600');

    // Color logic: open status
    const allTypesHaveOpen = typeStats.every(s => s.openCount > 0);
    const rawOpenStatus = selectedSection?.openStatus;
    const openStatusColor = allTypesHaveOpen
        ? 'text-green-600'
        : rawOpenStatus === 'C'
            ? 'text-red-600'
            : rawOpenStatus === 'W'
                ? 'text-orange-500'
                : 'text-gray-600';

    // Subtitle rows for time and open (all types when multiple required, else only types with >1 section)
    const subtitleStats = otherTypes.length > 0
        ? typeStats.filter(s => s.type !== firstType)
        : typeStats.filter(s => s.total > 1);
    const hasTimeSubtitle = subtitleStats.length > 0;
    const hasOpenSubtitle = subtitleStats.length > 0;

    return (
        <div className="bg-gray-50 rounded-lg py-3 mt-2">
            <div className="flex items-start justify-between mb-2 px-4">
                <div className="flex flex-col gap-0.5">
                    {sectionInfo && (
                        <span className="text-xs font-semibold text-gray-700">{sectionInfo}</span>
                    )}
                    {alsoRequiresLine && (
                        <span className="text-xs text-gray-500">{alsoRequiresLine}</span>
                    )}
                    {title && (
                        <span className="text-xs text-gray-500">{title}</span>
                    )}
                </div>
                <span className="text-xs font-semibold text-gray-700">{credits}</span>
            </div>
            <div className="flex items-start divide-x divide-gray-200">
                <div className="flex-1 px-4">
                    <div>
                        <span className={`text-base font-semibold ${timeColor}`}>{time ?? '--'}</span>
                        {hasTimeSubtitle && (
                            <div className="mt-1 flex flex-col gap-0.5">
                                {subtitleStats.map(s => (
                                    <span key={s.type} className="text-xs text-gray-500">
                                        <span className={s.fitCount === 0 ? 'text-red-600' : ''}>{s.fitCount}</span>
                                        /{s.total} {s.type} sections fit your schedule.
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 px-4">
                    <div>
                        <span className={`text-base font-semibold ${openStatusColor}`}>{openStatus ?? '--'}</span>
                        {hasOpenSubtitle && (
                            <div className="mt-1 flex flex-col gap-0.5">
                                {subtitleStats.map(s => (
                                    <span key={s.type} className="text-xs text-gray-500">
                                        <span className={s.openCount === 0 ? 'text-red-600' : ''}>{s.openCount}</span>
                                        /{s.total} {s.type} sections are open.
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="hidden md:block flex-1 px-4">
                    <div>
                        <span className="text-base font-semibold text-gray-600">{instructorName ?? '--'}</span>
                        {moreInstructorsCount > 0 && (
                            <div className="mt-1">
                                <span className="text-xs text-gray-500">and {moreInstructorsCount} more instructor{moreInstructorsCount > 1 ? 's' : ''}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EnrollGroupInfoBar;
